<script setup>
// Settings → Library — workspace (tenant) content policy. Today: the content-
// review opt-out. When ON, the DW-006 content-review gate no longer blocks audio
// generation — for a curated corporate knowledge base whose own positions are
// authoritative (and may not match an external "accuracy" check). Tenant-admin
// only; backed by GET/POST /api/library/settings (routes/decks.js).
import { ref, onMounted } from "vue";

const loading = ref(true);
const saving = ref(false);
const error = ref("");
const canEdit = ref(false);
const contentReviewDisabled = ref(false);

async function load() {
  loading.value = true; error.value = "";
  try {
    const r = await fetch("/api/library/settings", { credentials: "include" });
    if (!r.ok) throw new Error("Couldn't load workspace settings.");
    const d = await r.json();
    contentReviewDisabled.value = d.contentReviewDisabled === true;
    canEdit.value = d.canEdit === true;
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function toggle(next) {
  if (saving.value) return;
  saving.value = true; error.value = "";
  const prev = contentReviewDisabled.value;
  contentReviewDisabled.value = next; // optimistic
  try {
    const r = await fetch("/api/library/settings", {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contentReviewDisabled: next })
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || "Couldn't save that change.");
    contentReviewDisabled.value = d.contentReviewDisabled === true;
  } catch (e) {
    contentReviewDisabled.value = prev; // revert
    error.value = e.message;
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="card border-0 shadow-sm" style="max-width: 760px">
    <div class="card-body">
      <h2 class="h6 mb-1"><i class="bi bi-clipboard-check me-2"></i>Content review</h2>
      <p class="text-muted small mb-3">Controls whether decks must pass content review before their audio can be generated.</p>

      <div v-if="loading" class="text-muted small"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>

      <template v-else>
        <div class="form-check form-switch">
          <input
            id="reviewToggle" class="form-check-input" type="checkbox" role="switch"
            :checked="contentReviewDisabled" :disabled="!canEdit || saving"
            @change="toggle($event.target.checked)"
          >
          <label class="form-check-label" for="reviewToggle">
            <strong>Turn off content review for this workspace</strong>
          </label>
        </div>
        <p class="text-muted small mt-2 mb-0">
          When <strong>on</strong>, decks can generate audio without the AI accuracy check or human sign-off — appropriate for a
          curated knowledge base whose own positions are authoritative. The structural validity check (well-formed questions and
          options) always applies. When <strong>off</strong> (default), real content needs human approval and illustrative content
          needs a clean AI check first.
        </p>
        <p v-if="!canEdit" class="text-muted small mt-2 mb-0"><i class="bi bi-lock me-1"></i>Only a workspace admin can change this.</p>
        <div v-if="error" class="alert alert-warning py-2 mt-3 mb-0">{{ error }}</div>
        <div v-if="saving" class="text-muted small mt-2"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</div>
      </template>
    </div>
  </div>
</template>
